import { Suspense, useEffect } from 'react';
import { Outlet, useLocation, useNavigation } from 'react-router-dom';
import { useAuth } from '@weretradeit/hooks';
import { Footer, Loader, GlyphLoader, EmptyState, ErrorBoundary, RouteLoadingProgress, AppHeader, EnvironmentBadge, AppLauncher, StagingBanner, ThemeToggle, LanguageToggle, IconSprite, UserWidget, McpStatus, launcherApps } from '@weretradeit/ui-react';
import type { NavigationConfig } from '@weretradeit/ui-react';
import { getLoginURL } from '@weretradeit/env-helpers';
import { ladeGlyphSzeneImLeerlauf } from './glyphScene';
import './styles.css';

/* DIE ANWENDUNGSLISTE STEHT NICHT MEHR HIER — UND DAS WAR DER PUNKT.
 *
 * Der Kommentar an dieser Stelle nannte die Begruendung selbst: „Ein Launcher,
 * der je nach Anwendung etwas anderes anbietet, ist kein Launcher." Er hat sie
 * nur nicht zu Ende gefuehrt: die Liste blieb eine LOKALE Kopie, und die
 * Konvention „die eigene Anwendung zuerst" garantierte, dass die fuenf Kopien
 * sich in der Reihenfolge weiter widersprachen. Dazu kam eine Abweichung, die
 * keine Konvention war: shopBeaver fuehrte infrastructureDog ueber `APP_URLS`
 * (infra.weretrade.com), alle anderen ueber `PROD_URLS`
 * (infrastructure.weretrade.com) — dieselbe Kachel, zwei Ziele.
 *
 * `launcherApps('marketShark')` ist die eine Kopie: feste Reihenfolge,
 * `href: '/'` auf der eigenen App, und die Produktions-Hostnamen (die
 * `getPagesURL` ohne `customDomain` als `*.pages.dev` liefern wuerde) kennt
 * sie selbst. Seit dem 02.10.2026 kommt sie aus @weretradeit/ui-react
 * (Registry des Design-Systems, `v3/fleet-apps.json`) statt aus env-helpers:
 * dort standen sechs Apps, in der Registry alle zwoelf Launcher-Apps —
 * mailBird und fuenf weitere fehlten im Raster. Dieselbe Liste zeichnet
 * `FleetChrome`.
 *
 * Was hier gelernt wurde und im Paket weiterlebt: die Schluessel muessen in
 * Binnenmajuskel stehen (`marketShark`, nicht `market-shark`). `AppLauncher`
 * reicht sie unveraendert an `<AppMark app={a.key}>`, und AppMark kennt nur
 * die Namen des Design-Systems — mit Bindestrich zog das <img>
 * `/marks/market-shark.svg` und Cloudflare Pages antwortete 404, fuenf kaputte
 * Bildsymbole bei vorhandenen Dateien. */

/* Die acht `lazy(...)`-Zeilen fuer die Kacheln sind nach
   `pages/Dashboard.tsx` gewandert — dorthin, wo die Kacheln gezeichnet werden.
   Der Rahmen laedt jetzt SEITEN nach, nicht Kacheln (siehe routes.tsx). */

/* `RefreshIcon` ist entfallen. Es zeichnete einen 24x24-Pfeil von Hand und
   drehte ihn ueber `@keyframes spin` aus styles.css — beides macht jetzt
   `<Button loading>` aus ui-react, und das Design-System liefert ohnehin einen
   Sprite mit 48 Zeichen, der bereits im Dokument haengt. */

/**
 * Der RAHMEN der Anwendung — Kopfzeile, Fusszeile, Anmeldeschranke.
 *
 * Bis zum 08.09.2026 war diese Datei beides: Rahmen UND Dashboard. Genau
 * deshalb konnte es nur eine einzige Route geben — der Rahmen war das
 * Seitenelement. Vierzehn Seiten unter `src/pages/` waren dadurch gebaut,
 * gepflegt und unerreichbar.
 *
 * Der Inhalt sitzt jetzt in `pages/Dashboard.tsx`; hier steht `<Outlet />`.
 * Der Routenbaum liegt in `routes.tsx` und wird von `main.tsx` und
 * `App.test.tsx` gemeinsam benutzt.
 */
function App() {
  const auth = useAuth();
  const navState = useNavigation();
  const location = useLocation();

  // `useNavigation()` statt `useRevalidator()`: der Rahmen holt selbst keine
  // Daten mehr, und der Balken soll bei JEDEM Seitenwechsel laufen, nicht nur
  // beim Neuladen des Dashboards.
  const isNavigating = navState.state !== 'idle';

  /* Die GlyphField-Szene erst jetzt und erst im Leerlauf — nicht statisch aus
     main.tsx, dort zeichnete die Engine schon im ersten Wartebild
     (`hydrateFallbackElement`) und nahm dem Aufbau der Huelle den Hauptthread
     (accountingFox PR 790). Ab hier zeichnet das Feld der Sitzungspruefung,
     waehrend die App auf das Netz wartet. Siehe glyphScene.ts. */
  useEffect(() => {
    ladeGlyphSzeneImLeerlauf();
  }, []);

  // Loading state
  /* Vollbild-Warten wie in jeder App der Flotte (seit dem 02.10.2026),
     seit 4.2.0 mit der eigenen Szene: `GlyphLoader` statt des Kreisels
     `<Loader overlay="page">` — GLYPHFIELD.md § 3, „ein erster
     Ladebildschirm, der sonst nichts zu zeigen hat". Vorgabe overlay="page";
     der Text steht als role="status" daneben, das Feld ist Kunst (Szene:
     glyphScene.ts, geladen im Effekt darueber). „Session wird geprüft" ist
     der Text, auf den die E2E-Fixtures der Flotte (shop-auth) warten — er
     bleibt wortgleich. */
  if (auth.loading) {
    return <GlyphLoader appKey="marketShark" label="Session wird geprüft…" />;
  }

  /* Die Menuezeile fuehrte VIER Eintraege, und drei davon zeigten aus der
     Anwendung heraus (internes Dashboard, AccountingFox, InfrastructureDog).
     Von marketShark selbst stand genau einer drin: "Marketing" auf `/`.
     Newsletter und Social Media — beide gebaut, beide mit eigener Route —
     waren ueber die Oberflaeche nicht zu erreichen. Wer sie sehen wollte,
     musste die Adresse kennen.

     Jetzt zwei Abschnitte: erst die Seiten DIESER Anwendung, dann die
     Nachbaranwendungen. `AppLauncher` fuehrt dieselben Nachbarn noch einmal
     als Kachelraster — die Menuezeile ist der schnelle Weg, der Launcher der
     vollstaendige. */
  /* NUR DIE SEITEN DIESER ANWENDUNG.
   *
   * Bis zum 12.09.2026 fuehrte die Menuezeile einen ZWEITEN Abschnitt mit drei
   * Eintraegen, die aus marketShark HINAUS zeigten: internes Dashboard,
   * AccountingFox, InfrastructureDog. Dafuer gibt es den `<AppLauncher>` in der
   * Kopfzeile rechts — er fuehrt dieselben Nachbarn als Kachelraster, mit
   * Anwendungszeichen und Hervorhebung der eigenen. Zweimal dasselbe Angebot,
   * einmal als Reiterzeile getarnt: wer „Dashboard" in der Menuezeile las,
   * erwartete eine Seite DIESER Anwendung und landete in einer anderen.
   *
   * Der erste Eintrag hiess ausserdem „Marketing" und zeigte auf `/` — dieselbe
   * Seite, die jetzt auch in ihrer Ueberschrift „Dashboard" heisst. Ein Name
   * pro Ort. */
  const navigation: NavigationConfig = {
    sections: [
      {
        items: [
          /* Symbole wie in jeder anderen Menuezeile der Flotte — hier fehlten
             sie als einziger App mit Kopfzeilen-Navigation. Alle fuenf Namen
             stehen im Sprite (v3/icons/names.json, geprueft am 02.10.2026);
             ein unbekannter Name rendert als leere Flaeche ohne Fehler. */
          { label: "Dashboard", to: "/", icon: "dashboard" },
          { label: "Newsletter", to: "/newsletter", icon: "newsletter" },
          { label: "Social Media", to: "/social", icon: "share" },
          { label: "Affiliate", to: "/affiliate", icon: "link" },
          { label: "Deals", to: "/deals", icon: "tag" },
        ],
      },
    ],
  };
  /* Kopfzeile und Launcher standen zweimal woertlich gleich im Baum — einmal
     im Fehlerfall der Anmeldung, einmal im Normalfall. Der einzige
     Unterschied war `isAuthenticated`, das 3.x nicht mehr kennt: die
     Anwendungsliste kommt jetzt ausdruecklich. Damit sind die beiden Bloecke
     identisch und stehen nur noch einmal da. */
  const chrome = (
    <>
      {/* Der Sprite MUSS im Dokument stehen: <Icon> loest ueber
          <use href="#i-…"> auf, und der Launcher-Knopf IST ein Icon. */}
      <IconSprite src="/icons/sprite.svg" />
      {/* `activePath` war fest auf "/" verdrahtet. Solange es nur eine Route
          gab, fiel das nicht auf; mit dreien haette die Kopfzeile dauerhaft
          "Marketing" hervorgehoben — auch auf /newsletter und /social. */}
      <AppHeader
        appName="Market Shark"
        orgName="weretradeIT"
        mark="marketShark"
        navigation={navigation}
        activePath={location.pathname}
        actions={
          <>
            {/* `current` MUSS einer der Schluessel der Liste sein, sonst hebt
                der Launcher die eigene Anwendung nicht hervor — mit
                'market-shark' traf es keinen. */}
            <AppLauncher apps={launcherApps('marketShark')} current="marketShark" />
            {/* DIE REIHENFOLGE IST JETZT DIE DES DESIGN-SYSTEMS.
                Vorher: Launcher, Umgebungskennzeichen, Sprache, Thema.
                `app-shell.css` ordnet `.app-header__actions` ausdruecklich
                Launcher 1, `.segmented--codes` (Sprache) 2, `.segmented`
                (Thema) 3, `.env-badge` 4, Nutzer 5 — die CSS-Regel hat den
                Unterschied optisch geheilt und die DOM-Reihenfolge, an der
                Tab-Navigation und Vorlesereihenfolge haengen, blieb falsch. */}
            {/* Der Umschalter ist seit 3.12.0 UNKONTROLLIERT: er haelt
                `wt-lang` selbst, setzt `<html lang>`/`data-lang` und folgt
                einer Aenderung aus einer Nachbar-App oder einem anderen Reiter.
                `src/hooks/useLanguage.ts` — der lokale Zustand, den die alte
                gesteuerte Signatur erzwang — ist damit ersatzlos entfallen;
                er war eine wortgleiche Kopie derselben Datei in
                infrastructureDog, und beide bauten `wt-lang` nach.

                EHRLICH GESAGT: marketSharks eigene Beschriftungen sind
                bislang nur englisch. Der Umschalter wirkt auf die
                `:lang()`-Typografie, die Zeichenketten des Design-Systems und
                die flottenweite Wahl — aber die Saetze dieser Anwendung
                uebersetzt er noch nicht. Das ist der ehrliche Zwischenstand
                und kein fertiges Mehrsprachenwerk. */}
            <LanguageToggle />
          </>
        }
        /* Die Slots wie in `FleetChrome` (ui-react 4.0.0): Thema in
           `themeSlot`, Umgebung + Benutzer in `userSlot`. Ohne Slot haengt
           AppHeader HINTER `actions` leere Montagepunkte der Vanilla-Widgets
           an (`#theme-toggle-mount`, `#wt-user-widget-mount`). Fuer den
           Branding-Anker `brandingSlot={false}` — nicht `null`, weil 4.0.0
           null wie „kein Slot" behandelt. */
        themeSlot={<ThemeToggle />}
        userSlot={
          <>
            {/* Der MCP-Punkt (4.1.0, FLEET-CONTRACT § 11) — als ERSTES im
                Slot, wie `FleetChrome` ihn setzt: zwischen Thema und
                Umgebung. Er fragt `/api/mcp/health` (Pages-Funktion ->
                market-shark-api) mit dem Sitzungscookie, alle 60 s. Das MCP
                ist `kleinanzeigen-mcp`; was der Endpunkt prüft und warum
                er heute „Fehler" zeigt, steht in
                workers/market-shark-api/mcp-health.ts. */}
            <McpStatus appKey="marketShark" />
            <EnvironmentBadge hideInProduction />
            {/* shared/widgets/user-widget.js (Kopie unter public/assets) ist mit
                design-system 3.15.0 / ui-react 3.15.0 entfallen (MIGRATION.md
                "shared/widgets" -> UserWidget/AppBranding).

                Aufruf wie in `FleetChrome`: Profil und Abmelden aus den
                Vorgaben des Bauteils. Hier standen `profileHref` auf
                `…/profile` (eine Seite, die authLion nicht kennt; die Vorgabe
                ist `…/account`) und ein `onLogout`, der nur umleitete, ohne
                `/logout` zu rufen — die Sitzung blieb gueltig. Der eingebaute
                Logout ruft `POST {authBaseUrl}/logout` und leitet dann um.
                Kein Schnellzugriffsraster: dasselbe Angebot steht links
                daneben im Launcher.

                Die Stelle 5 im Cluster gibt app-shell.css erst ab 4.0.1 —
                bis dahin haelt styles.css `.user-widget` dort. */}
            <UserWidget authBaseUrl="https://login.weretrade.com/api/auth" showQuickApps={false} />
          </>
        }
        brandingSlot={false}
      />
    </>
  );

  // Auth error state
  if (auth.error && !auth.isAuthenticated) {
    return (
      <div className="app-shell">
        {chrome}
        <main className="app-main">
          {/* War ein handgebauter Block: `<span style={{fontSize:64}}>🔒</span>`
              ueber rohem <h2>/<p> in einem `.error-state`-Kasten — einer
              v2-Altlast aus compat/v2-aliases.css. `EmptyState` ist die Form
              des Design-Systems fuer genau diesen Fall und bringt Zeichen,
              Ueberschrift, Erklaerung und EINE Handlung mit. Der Anmeldelink
              traegt jetzt ausserdem eine Knopfform: `className="btn"` allein
              hat in v3 keine Farbe, es braucht eine Variante. */}
          <EmptyState
            mark="🔒"
            title="Authentication required"
            description={auth.error}
            action={<a href={getLoginURL()} className="btn btn-primary">Sign in</a>}
          />
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <>
      <StagingBanner />
      {/* Die vom Design-System vorgegebene Reihenfolge (app-shell.css):
          StagingBanner -> RouteLoadingProgress -> AppHeader -> main -> Footer. */}
      <RouteLoadingProgress active={isNavigating} />
      <div className="app-shell">
        {chrome}

        <main className="app-main">
          {/* Ohne Fangnetz landete ein Fehler beim Zeichnen auf der ungestylten
              Vorgabeseite von react-router — die Routen haben zwar einen
              Loader, aber kein `errorElement`. ErrorBoundary zeigt stattdessen
              eine Meldung mit Vorgangsnummer.

              Das Suspense hier ist NICHT dasselbe wie das im Dashboard: dieses
              faengt die nachgeladene SEITE (routes.tsx laedt jede per lazy),
              jenes die einzelnen Kacheln. */}
          {/* Seit 4.2.0 mit `appKey` und OHNE eigenes `fallback` (ein
              `fallback` gewaenne vor `appKey`): ein Fehler zeichnet
              `GlyphErrorPage kind="500"` mit der Szene dieser App, der
              Kennung zum Melden und „Erneut versuchen" — innerhalb des
              Rahmens, Kopfzeile und Menue bleiben stehen. */}
          <ErrorBoundary appKey="marketShark">
            <Suspense fallback={<Loader size="lg" label="Seite wird geladen" />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default App;
